import React, { useState, useEffect } from 'react';

const PIECES = {
  K: '\u2654', Q: '\u2655', R: '\u2656', B: '\u2657', N: '\u2658', P: '\u2659',
  k: '\u265A', q: '\u265B', r: '\u265C', b: '\u265D', n: '\u265E', p: '\u265F'
};

const INITIAL_BOARD = [
  ['r', 'n', 'b', 'q', 'k', 'b', 'n', 'r'],
  ['p', 'p', 'p', 'p', 'p', 'p', 'p', 'p'],
  [null, null, null, null, null, null, null, null],
  [null, null, null, null, null, null, null, null],
  [null, null, null, null, null, null, null, null],
  [null, null, null, null, null, null, null, null],
  ['P', 'P', 'P', 'P', 'P', 'P', 'P', 'P'],
  ['R', 'N', 'B', 'Q', 'K', 'B', 'N', 'R']
];

const PIECE_VALUES = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0, P: 1, N: 3, B: 3, R: 5, Q: 9, K: 0 };

const isWhite = (p) => p && p === p.toUpperCase();
const isBlack = (p) => p && p === p.toLowerCase();
const isOwn = (p, white) => white ? isWhite(p) : isBlack(p);
const isEnemy = (p, white) => p && (white ? isBlack(p) : isWhite(p));

function cloneBoard(board) {
  return board.map(row => [...row]);
}

function findKing(board, white) {
  const king = white ? 'K' : 'k';
  for (let r = 0; r < 8; r++)
    for (let c = 0; c < 8; c++)
      if (board[r][c] === king) return [r, c];
  return null;
}

function isInBounds(r, c) {
  return r >= 0 && r < 8 && c >= 0 && c < 8;
}

function getRawMoves(board, row, col, castling, enPassant) {
  const piece = board[row][col];
  if (!piece) return [];
  const white = isWhite(piece);
  const moves = [];
  const type = piece.toLowerCase();

  if (type === 'p') {
    const dir = white ? -1 : 1;
    const startRow = white ? 6 : 1;
    if (isInBounds(row + dir, col) && !board[row + dir][col]) {
      moves.push([row + dir, col]);
      if (row === startRow && !board[row + 2 * dir][col]) {
        moves.push([row + 2 * dir, col]);
      }
    }
    for (const dc of [-1, 1]) {
      const nr = row + dir, nc = col + dc;
      if (isInBounds(nr, nc)) {
        if (isEnemy(board[nr][nc], white)) moves.push([nr, nc]);
        if (enPassant && enPassant[0] === nr && enPassant[1] === nc) moves.push([nr, nc, 'ep']);
      }
    }
  } else if (type === 'n') {
    for (const [dr, dc] of [[-2,-1],[-2,1],[-1,-2],[-1,2],[1,-2],[1,2],[2,-1],[2,1]]) {
      const nr = row + dr, nc = col + dc;
      if (isInBounds(nr, nc) && !isOwn(board[nr][nc], white)) moves.push([nr, nc]);
    }
  } else if (type === 'b') {
    for (const [dr, dc] of [[-1,-1],[-1,1],[1,-1],[1,1]]) {
      for (let i = 1; i < 8; i++) {
        const nr = row + dr * i, nc = col + dc * i;
        if (!isInBounds(nr, nc)) break;
        if (isOwn(board[nr][nc], white)) break;
        moves.push([nr, nc]);
        if (isEnemy(board[nr][nc], white)) break;
      }
    }
  } else if (type === 'r') {
    for (const [dr, dc] of [[-1,0],[1,0],[0,-1],[0,1]]) {
      for (let i = 1; i < 8; i++) {
        const nr = row + dr * i, nc = col + dc * i;
        if (!isInBounds(nr, nc)) break;
        if (isOwn(board[nr][nc], white)) break;
        moves.push([nr, nc]);
        if (isEnemy(board[nr][nc], white)) break;
      }
    }
  } else if (type === 'q') {
    for (const [dr, dc] of [[-1,-1],[-1,0],[-1,1],[0,-1],[0,1],[1,-1],[1,0],[1,1]]) {
      for (let i = 1; i < 8; i++) {
        const nr = row + dr * i, nc = col + dc * i;
        if (!isInBounds(nr, nc)) break;
        if (isOwn(board[nr][nc], white)) break;
        moves.push([nr, nc]);
        if (isEnemy(board[nr][nc], white)) break;
      }
    }
  } else if (type === 'k') {
    for (const [dr, dc] of [[-1,-1],[-1,0],[-1,1],[0,-1],[0,1],[1,-1],[1,0],[1,1]]) {
      const nr = row + dr, nc = col + dc;
      if (isInBounds(nr, nc) && !isOwn(board[nr][nc], white)) moves.push([nr, nc]);
    }
    const kRow = white ? 7 : 0;
    if (row === kRow && col === 4) {
      if (castling[white ? 'wK' : 'bK'] && !board[kRow][5] && !board[kRow][6] && board[kRow][7] &&
          board[kRow][7].toLowerCase() === 'r' && isWhite(board[kRow][7]) === white) {
        if (!isSquareAttacked(board, kRow, 4, !white) && !isSquareAttacked(board, kRow, 5, !white) && !isSquareAttacked(board, kRow, 6, !white))
          moves.push([kRow, 6, 'castle']);
      }
      if (castling[white ? 'wQ' : 'bQ'] && !board[kRow][3] && !board[kRow][2] && !board[kRow][1] && board[kRow][0] &&
          board[kRow][0].toLowerCase() === 'r' && isWhite(board[kRow][0]) === white) {
        if (!isSquareAttacked(board, kRow, 4, !white) && !isSquareAttacked(board, kRow, 3, !white) && !isSquareAttacked(board, kRow, 2, !white))
          moves.push([kRow, 2, 'castle']);
      }
    }
  }

  return moves;
}

function isSquareAttacked(board, row, col, byWhite) {
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const p = board[r][c];
      if (!p || (byWhite ? isBlack(p) : isWhite(p))) continue;
      const type = p.toLowerCase();
      const dr = row - r, dc = col - c;
      const adr = Math.abs(dr), adc = Math.abs(dc);

      if (type === 'p') {
        const dir = byWhite ? -1 : 1;
        if (dr === dir && adc === 1) return true;
      } else if (type === 'n') {
        if ((adr === 2 && adc === 1) || (adr === 1 && adc === 2)) return true;
      } else if (type === 'b') {
        if (adr === adc && adr > 0) {
          const sr = dr > 0 ? 1 : -1, sc = dc > 0 ? 1 : -1;
          let blocked = false;
          for (let i = 1; i < adr; i++) { if (board[r + sr * i][c + sc * i]) { blocked = true; break; } }
          if (!blocked) return true;
        }
      } else if (type === 'r') {
        if ((dr === 0 || dc === 0) && (adr + adc > 0)) {
          const sr = dr === 0 ? 0 : (dr > 0 ? 1 : -1);
          const sc = dc === 0 ? 0 : (dc > 0 ? 1 : -1);
          let blocked = false;
          const steps = Math.max(adr, adc);
          for (let i = 1; i < steps; i++) { if (board[r + sr * i][c + sc * i]) { blocked = true; break; } }
          if (!blocked) return true;
        }
      } else if (type === 'q') {
        if ((adr === adc && adr > 0) || ((dr === 0 || dc === 0) && (adr + adc > 0))) {
          const sr = dr === 0 ? 0 : (dr > 0 ? 1 : -1);
          const sc = dc === 0 ? 0 : (dc > 0 ? 1 : -1);
          let blocked = false;
          const steps = Math.max(adr, adc);
          for (let i = 1; i < steps; i++) { if (board[r + sr * i][c + sc * i]) { blocked = true; break; } }
          if (!blocked) return true;
        }
      } else if (type === 'k') {
        if (adr <= 1 && adc <= 1 && (adr + adc > 0)) return true;
      }
    }
  }
  return false;
}

function isInCheck(board, white) {
  const king = findKing(board, white);
  if (!king) return false;
  return isSquareAttacked(board, king[0], king[1], !white);
}

function makeMove(board, fromR, fromC, toR, toC, special, enPassant) {
  const nb = cloneBoard(board);
  const piece = nb[fromR][fromC];

  if (special === 'ep') {
    nb[fromR][toC] = null;
  } else if (special === 'castle') {
    if (toC === 6) { nb[fromR][5] = nb[fromR][7]; nb[fromR][7] = null; }
    else if (toC === 2) { nb[fromR][3] = nb[fromR][0]; nb[fromR][0] = null; }
  }

  nb[toR][toC] = piece;
  nb[fromR][fromC] = null;

  if (piece && piece.toLowerCase() === 'p' && (toR === 0 || toR === 7)) {
    nb[toR][toC] = isWhite(piece) ? 'Q' : 'q';
  }

  return nb;
}

function getLegalMoves(board, row, col, castling, enPassant) {
  const piece = board[row][col];
  if (!piece) return [];
  const white = isWhite(piece);
  const raw = getRawMoves(board, row, col, castling, enPassant);
  const legal = [];

  for (const move of raw) {
    const [tr, tc, special] = move;
    const nb = makeMove(board, row, col, tr, tc, special, enPassant);
    if (!isInCheck(nb, white)) {
      legal.push(move);
    }
  }
  return legal;
}

function getAllLegalMoves(board, white, castling, enPassant) {
  const moves = [];
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      if (board[r][c] && (white ? isWhite(board[r][c]) : isBlack(board[r][c]))) {
        const pieceMoves = getLegalMoves(board, r, c, castling, enPassant);
        for (const m of pieceMoves) {
          moves.push({ from: [r, c], to: [m[0], m[1]], special: m[2] });
        }
      }
    }
  }
  return moves;
}

function evaluate(board) {
  let score = 0;
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const p = board[r][c];
      if (!p) continue;
      const val = PIECE_VALUES[p] || 0;
      const centerBonus = (3.5 - Math.abs(r - 3.5)) * 0.1 + (3.5 - Math.abs(c - 3.5)) * 0.1;
      if (isWhite(p)) {
        score += val + centerBonus;
      } else {
        score -= val + centerBonus;
      }
    }
  }
  return score;
}

function minimax(board, depth, alpha, beta, maximizing, castling, enPassant) {
  const white = maximizing;
  const moves = getAllLegalMoves(board, white, castling, enPassant);

  if (moves.length === 0) {
    return isInCheck(board, white) ? (maximizing ? -9999 + (10 - depth) : 9999 - (10 - depth)) : 0;
  }
  if (depth === 0) return evaluate(board);

  if (maximizing) {
    let maxEval = -Infinity;
    for (const move of moves) {
      const nb = makeMove(board, move.from[0], move.from[1], move.to[0], move.to[1], move.special, enPassant);
      const ev = minimax(nb, depth - 1, alpha, beta, false, castling, null);
      maxEval = Math.max(maxEval, ev);
      alpha = Math.max(alpha, ev);
      if (beta <= alpha) break;
    }
    return maxEval;
  } else {
    let minEval = Infinity;
    for (const move of moves) {
      const nb = makeMove(board, move.from[0], move.from[1], move.to[0], move.to[1], move.special, enPassant);
      const ev = minimax(nb, depth - 1, alpha, beta, true, castling, null);
      minEval = Math.min(minEval, ev);
      beta = Math.min(beta, ev);
      if (beta <= alpha) break;
    }
    return minEval;
  }
}

function getBestMove(board, castling, enPassant, depth = 3) {
  const moves = getAllLegalMoves(board, false, castling, enPassant);
  if (moves.length === 0) return null;

  let bestMove = moves[0];
  let bestScore = Infinity;

  for (const move of moves) {
    const nb = makeMove(board, move.from[0], move.from[1], move.to[0], move.to[1], move.special, enPassant);
    const score = minimax(nb, depth - 1, -Infinity, Infinity, true, castling, null);
    if (score < bestScore) {
      bestScore = score;
      bestMove = move;
    }
  }
  return bestMove;
}

function updateCastling(castling, fromR, fromC, toR, toC, piece) {
  const nc = { ...castling };
  if (piece === 'K') { nc.wK = false; nc.wQ = false; }
  if (piece === 'k') { nc.bK = false; nc.bQ = false; }
  if (piece === 'R' && fromR === 7 && fromC === 7) nc.wK = false;
  if (piece === 'R' && fromR === 7 && fromC === 0) nc.wQ = false;
  if (piece === 'r' && fromR === 0 && fromC === 7) nc.bK = false;
  if (piece === 'r' && fromR === 0 && fromC === 0) nc.bQ = false;
  if (toR === 0 && toC === 7) nc.bK = false;
  if (toR === 0 && toC === 0) nc.bQ = false;
  if (toR === 7 && toC === 7) nc.wK = false;
  if (toR === 7 && toC === 0) nc.wQ = false;
  return nc;
}

export default function Chess() {
  const [gameMode, setGameMode] = useState(null);
  const [board, setBoard] = useState(INITIAL_BOARD);
  const [isWhiteTurn, setIsWhiteTurn] = useState(true);
  const [selected, setSelected] = useState(null);
  const [legalMoves, setLegalMoves] = useState([]);
  const [castling, setCastling] = useState({ wK: true, wQ: true, bK: true, bQ: true });
  const [enPassant, setEnPassant] = useState(null);
  const [capturedWhite, setCapturedWhite] = useState([]);
  const [capturedBlack, setCapturedBlack] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [gameResult, setGameResult] = useState('');
  const [aiThinking, setAiThinking] = useState(false);
  const [difficulty, setDifficulty] = useState('medium');
  const [difficultySelected, setDifficultySelected] = useState(false);
  const [lastMove, setLastMove] = useState(null);
  const [moveHistory, setMoveHistory] = useState([])
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  }, []);

  const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

  const handleSquareClick = (row, col) => {
    if (gameOver || aiThinking) return;
    if (gameMode === 'ai' && !isWhiteTurn) return;

    const piece = board[row][col];

    if (selected) {
      const move = legalMoves.find(m => m[0] === row && m[1] === col);
      if (move) {
        executeMove(selected[0], selected[1], row, col, move[2]);
        return;
      }
      if (piece && isOwn(piece, isWhiteTurn)) {
        selectPiece(row, col);
        return;
      }
      setSelected(null);
      setLegalMoves([]);
      return;
    }

    if (piece && isOwn(piece, isWhiteTurn)) {
      selectPiece(row, col);
    }
  };

  const selectPiece = (row, col) => {
    setSelected([row, col]);
    const moves = getLegalMoves(board, row, col, castling, enPassant);
    setLegalMoves(moves);
  };

  const executeMove = (fromR, fromC, toR, toC, special) => {
    const piece = board[fromR][fromC];
    const captured = board[toR][toC];
    const nb = makeMove(board, fromR, fromC, toR, toC, special, enPassant);

    if (special === 'ep') {
      const epPiece = board[fromR][toC];
      if (isWhite(epPiece)) setCapturedWhite(prev => [...prev, epPiece]);
      else setCapturedBlack(prev => [...prev, epPiece]);
    } else if (captured) {
      if (isWhite(captured)) setCapturedWhite(prev => [...prev, captured]);
      else setCapturedBlack(prev => [...prev, captured]);
    }

    const nc = updateCastling(castling, fromR, fromC, toR, toC, piece);

    let newEnPassant = null;
    if (piece && piece.toLowerCase() === 'p' && Math.abs(toR - fromR) === 2) {
      newEnPassant = [(fromR + toR) / 2, fromC];
    }

    const moveNotation = `${PIECES[piece]}${files[fromC]}${8 - fromR}\u2192${files[toC]}${8 - toR}`;
    setMoveHistory(prev => [...prev, moveNotation]);
    setLastMove({ from: [fromR, fromC], to: [toR, toC] });

    setBoard(nb);
    setCastling(nc);
    setEnPassant(newEnPassant);
    setSelected(null);
    setLegalMoves([]);

    const nextWhite = !isWhiteTurn;
    setIsWhiteTurn(nextWhite);

    const nextMoves = getAllLegalMoves(nb, nextWhite, nc, newEnPassant);
    if (nextMoves.length === 0) {
      setGameOver(true);
      if (isInCheck(nb, nextWhite)) {
        setGameResult(isWhiteTurn ? 'BLACK WINS!' : 'WHITE WINS!');
      } else {
        setGameResult('STALEMATE!');
      }
    } else if (isInCheck(nb, nextWhite)) {
      setGameResult(isWhiteTurn ? 'BLACK IN CHECK!' : 'WHITE IN CHECK!');
      setTimeout(() => setGameResult(''), 2000);
    }
  };

  useEffect(() => {
    if (gameMode !== 'ai' || isWhiteTurn || gameOver || !difficultySelected) return;

    setAiThinking(true);
    const depth = difficulty === 'easy' ? 1 : difficulty === 'medium' ? 2 : 3;
    const timer = setTimeout(() => {
      const move = getBestMove(board, castling, enPassant, depth);
      if (move) {
        executeMove(move.from[0], move.from[1], move.to[0], move.to[1], move.special);
      }
      setAiThinking(false);
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWhiteTurn, gameMode, gameOver, difficultySelected, board, castling, enPassant, difficulty]);

  const resetGame = () => {
    setBoard(INITIAL_BOARD);
    setIsWhiteTurn(true);
    setSelected(null);
    setLegalMoves([]);
    setCastling({ wK: true, wQ: true, bK: true, bQ: true });
    setEnPassant(null);
    setCapturedWhite([]);
    setCapturedBlack([]);
    setGameOver(false);
    setGameResult('');
    setLastMove(null);
    setMoveHistory([]);
  };

  const fullReset = () => {
    resetGame();
    setGameMode(null);
    setDifficultySelected(false);
  };

  const isLegalTarget = (r, c) => legalMoves.some(m => m[0] === r && m[1] === c);
  const isLastMoveSquare = (r, c) => lastMove && ((lastMove.from[0] === r && lastMove.from[1] === c) || (lastMove.to[0] === r && lastMove.to[1] === c));

  const sortCaptured = (arr) => [...arr].sort((a, b) => (PIECE_VALUES[b] || 0) - (PIECE_VALUES[a] || 0));

  if (!gameMode) {
    return (
      <div className="flex flex-col items-center" style={{ maxWidth: '400px', margin: '0 auto', width: '100%', padding: '1rem' }}>
        <p className="text-center text-slate-400 mb-6 font-mono">Choose game mode:</p>
        <div className="flex gap-4 justify-center">
          <button
            onClick={() => setGameMode('ai')}
            className="px-6 py-3 rounded-lg font-bold transition-all duration-200"
            style={{
              background: 'transparent', border: '2px solid #00d9ff', color: '#00d9ff',
              boxShadow: '0 0 10px rgba(0,217,255,0.3)'
            }}
            onMouseEnter={(e) => { e.target.style.background = 'rgba(0,217,255,0.1)'; e.target.style.boxShadow = '0 0 20px rgba(0,217,255,0.6)'; }}
            onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.boxShadow = '0 0 10px rgba(0,217,255,0.3)'; }}
          >
            VS AI
          </button>
          <button
            onClick={() => { setGameMode('friend'); resetGame(); }}
            className="px-6 py-3 rounded-lg font-bold transition-all duration-200"
            style={{
              background: 'transparent', border: '2px solid #ff006e', color: '#ff006e',
              boxShadow: '0 0 10px rgba(255,0,110,0.3)'
            }}
            onMouseEnter={(e) => { e.target.style.background = 'rgba(255,0,110,0.1)'; e.target.style.boxShadow = '0 0 20px rgba(255,0,110,0.6)'; }}
            onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.boxShadow = '0 0 10px rgba(255,0,110,0.3)'; }}
          >
            VS Friend
          </button>
        </div>
      </div>
    );
  }

  if (gameMode === 'ai' && !difficultySelected) {
    return (
      <div className="flex flex-col items-center" style={{ maxWidth: '400px', margin: '0 auto', width: '100%', padding: '1rem' }}>
        <p className="text-center text-slate-400 mb-6 font-mono">Select difficulty:</p>
        <div className="flex gap-3 justify-center">
          {['easy', 'medium', 'hard'].map((diff) => (
            <button
              key={diff}
              onClick={() => { setDifficulty(diff); setDifficultySelected(true); resetGame(); }}
              className="px-4 py-2 rounded-lg font-bold transition-all duration-200 capitalize"
              style={{
                background: difficulty === diff ? 'rgba(0,217,255,0.2)' : 'transparent',
                border: `2px solid ${difficulty === diff ? '#00d9ff' : 'rgba(0,217,255,0.3)'}`,
                color: '#00d9ff',
                boxShadow: difficulty === diff ? '0 0 15px rgba(0,217,255,0.5)' : 'none'
              }}
            >
              {diff}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center" style={{ maxWidth: '450px', margin: '0 auto', width: '100%', padding: '0.5rem' }}>
      <div className="flex justify-between items-center w-full mb-3 px-1" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <div className="flex gap-1 flex-wrap" style={{ maxWidth: isMobile ? '40%' : '60%' }}>
          {sortCaptured(capturedBlack).map((p, i) => (
            <span key={i} style={{ color: '#00d9ff', fontSize: 'clamp(14px, 4vw, 20px)', textShadow: '0 0 8px rgba(0,217,255,0.5)' }}>{PIECES[p]}</span>
          ))}
        </div>
        <div
          className="px-3 py-1 rounded-lg font-mono text-sm font-bold"
          style={{
            fontSize: 'clamp(10px, 3vw, 14px)',
            color: isWhiteTurn ? '#00d9ff' : '#ff006e',
            textShadow: `0 0 10px ${isWhiteTurn ? '#00d9ff' : '#ff006e'}`,
            border: `1px solid ${isWhiteTurn ? 'rgba(0,217,255,0.4)' : 'rgba(255,0,110,0.4)'}`,
          }}
        >
          {gameOver ? gameResult : aiThinking ? 'AI THINKING...' : `${isWhiteTurn ? 'WHITE' : 'BLACK'} TO MOVE`}
        </div>
        <div className="flex gap-1 flex-wrap" style={{ maxWidth: isMobile ? '40%' : '60%' }}>
          {sortCaptured(capturedWhite).map((p, i) => (
            <span key={i} style={{ color: '#ff006e', fontSize: 'clamp(14px, 4vw, 20px)', textShadow: '0 0 8px rgba(255,0,110,0.5)' }}>{PIECES[p]}</span>
          ))}
        </div>
      </div>

      <div
        className="rounded-lg overflow-hidden"
        style={{
          border: '2px solid rgba(0,217,255,0.4)',
          boxShadow: '0 0 30px rgba(0,217,255,0.2)',
          touchAction: 'none',
        }}
      >
        {board.map((row, r) => (
          <div key={r} className="flex">
            {row.map((piece, c) => {
              const isLight = (r + c) % 2 === 0;
              const isSelected = selected && selected[0] === r && selected[1] === c;
              const isLegal = isLegalTarget(r, c);
              const isLast = isLastMoveSquare(r, c);
              const isKingInCheck = piece && ((piece === 'K' && isInCheck(board, true) && isWhiteTurn) || (piece === 'k' && isInCheck(board, false) && !isWhiteTurn));

              let bg = isLight ? 'rgba(30,40,80,0.8)' : 'rgba(10,14,39,0.95)';
              if (isSelected) bg = 'rgba(0,217,255,0.3)';
              else if (isLast) bg = isLight ? 'rgba(0,217,255,0.15)' : 'rgba(0,217,255,0.1)';
              if (isKingInCheck) bg = 'rgba(255,0,110,0.3)';

              return (
                <div
                  key={c}
                  onClick={() => handleSquareClick(r, c)}
                  className="relative flex items-center justify-center cursor-pointer transition-all duration-150"
                  style={{
                    width: 'clamp(40px, 11vw, 50px)', height: 'clamp(40px, 11vw, 50px)',
                    background: bg,
                    border: '1px solid rgba(0,217,255,0.1)',
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = isLight ? 'rgba(0,217,255,0.15)' : 'rgba(0,217,255,0.1)'; }}
                  onMouseLeave={(e) => { if (!isSelected && !isLast) e.currentTarget.style.background = bg; }}
                >
                  {piece && (
                    <span
                      style={{
                        fontSize: 'clamp(24px, 7vw, 32px)',
                        lineHeight: 1,
                        color: isWhite(piece) ? '#00d9ff' : '#ff006e',
                        textShadow: `0 0 15px ${isWhite(piece) ? '#00d9ff' : '#ff006e'}`,
                        filter: isKingInCheck ? 'brightness(1.5)' : 'none',
                        transition: 'all 0.15s',
                        userSelect: 'none',
                      }}
                    >
                      {PIECES[piece]}
                    </span>
                  )}
                  {isLegal && !piece && (
                    <div
                      style={{
                        width: '14px', height: '14px', borderRadius: '50%',
                        background: 'rgba(0,217,255,0.4)',
                        boxShadow: '0 0 10px rgba(0,217,255,0.5)',
                      }}
                    />
                  )}
                  {isLegal && piece && (
                    <div
                      style={{
                        position: 'absolute', inset: 2, borderRadius: '50%',
                        border: '3px solid rgba(0,217,255,0.6)',
                        boxShadow: '0 0 10px rgba(0,217,255,0.4)',
                      }}
                    />
                  )}
                  {c === 0 && (
                    <span
                      style={{
                        position: 'absolute', top: 2, left: 3,
                        fontSize: '9px', fontFamily: 'monospace',
                        color: isLight ? 'rgba(0,217,255,0.4)' : 'rgba(0,217,255,0.3)',
                      }}
                    >
                      {8 - r}
                    </span>
                  )}
                  {r === 7 && (
                    <span
                      style={{
                        position: 'absolute', bottom: 1, right: 3,
                        fontSize: '9px', fontFamily: 'monospace',
                        color: isLight ? 'rgba(0,217,255,0.4)' : 'rgba(0,217,255,0.3)',
                      }}
                    >
                      {files[c]}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {gameOver && (
        <div
          className="mt-6 p-4 rounded-lg text-center"
          style={{
            background: 'rgba(10,14,39,0.95)',
            border: `1px solid ${gameResult.includes('WHITE') ? '#00d9ff' : gameResult.includes('BLACK') ? '#ff006e' : '#d000ff'}`,
            boxShadow: `0 0 20px ${gameResult.includes('WHITE') ? 'rgba(0,217,255,0.3)' : gameResult.includes('BLACK') ? 'rgba(255,0,110,0.3)' : 'rgba(208,0,255,0.3)'}`,
          }}
        >
          <p
            className="text-2xl font-bold mb-4"
            style={{
              color: gameResult.includes('WHITE') ? '#00d9ff' : gameResult.includes('BLACK') ? '#ff006e' : '#d000ff',
              textShadow: `0 0 15px ${gameResult.includes('WHITE') ? '#00d9ff' : gameResult.includes('BLACK') ? '#ff006e' : '#d000ff'}`,
            }}
          >
            {gameResult}
          </p>
          <button
            onClick={resetGame}
            className="px-6 py-2 rounded-lg font-bold transition-all duration-200"
            style={{
              background: 'transparent',
              border: '2px solid #00d9ff',
              color: '#00d9ff',
              boxShadow: '0 0 10px rgba(0,217,255,0.3)',
            }}
            onMouseEnter={(e) => { e.target.style.background = 'rgba(0,217,255,0.1)'; e.target.style.boxShadow = '0 0 20px rgba(0,217,255,0.6)'; }}
            onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.boxShadow = '0 0 10px rgba(0,217,255,0.3)'; }}
          >
            NEW GAME
          </button>
        </div>
      )}

      {moveHistory.length > 0 && (
        <div
          className="mt-4 p-3 rounded-lg w-full max-w-md"
          style={{
            background: 'rgba(10,14,39,0.8)',
            border: '1px solid rgba(0,217,255,0.2)',
            maxHeight: '80px',
            overflowY: 'auto',
          }}
        >
          <div className="flex flex-wrap gap-1 font-mono text-xs">
            {moveHistory.map((m, i) => (
              <span key={i} style={{ color: i % 2 === 0 ? '#00d9ff' : '#ff006e' }}>{Math.floor(i / 2) + 1}.{m}</span>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-4 mt-4">
        <button
          onClick={resetGame}
          className="px-4 py-2 rounded-lg font-mono text-sm transition-all duration-200"
          style={{
            background: 'transparent', border: '2px solid rgba(0,217,255,0.5)', color: '#94a3b8',
          }}
        >
          RESET BOARD
        </button>
      </div>

      <button
        onClick={fullReset}
        className="mt-3 text-sm text-slate-500 hover:text-slate-300 transition-colors"
      >
        \u2190 Back to mode selection
      </button>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
